import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { DIVISIONI } from "@/data/content";
import { Reveal, GoldLine } from "@/components/Reveal";
import { useLang } from "@/i18n";

export default function BusinessUnit() {
  const { t } = useLang();
  const { id } = useParams();
  const unit = DIVISIONI.find((d) => d.id === id);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  if (!unit) return <Navigate to="/" replace />;

  const index = DIVISIONI.indexOf(unit);
  const next = DIVISIONI[(index + 1) % DIVISIONI.length];

  return (
    <main data-testid={`business-unit-${unit.id}`} className="bg-[#080808]">
      {/* HERO */}
      <section ref={ref} className="relative h-[72vh] min-h-[520px] overflow-hidden">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img src={unit.img} alt={`Billions ${unit.nome}`} className="w-full h-full object-cover scale-105 brightness-[0.55]" />
        </motion.div>
        <div className="absolute inset-0 bg-[#080808]/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/60 via-transparent to-[#080808]" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="eyebrow mb-6"
          >
            Business Unit {unit.numero} — {unit.sottotitolo}
          </motion.p>          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-8xl font-light tracking-tight text-[#F5F2EB]"
          >
            Billions <span className="gold-gradient-text italic">{unit.nome}.</span>
          </motion.h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="gold-line origin-left w-40 mt-8"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-6 font-display text-xl sm:text-2xl font-light italic text-[#C6A45D]"
          >
            {unit.payoff}
          </motion.p>
        </div>
      </section>

      {/* DESCRIZIONE */}
      <section className="bg-[#080808] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="font-display text-7xl lg:text-8xl font-light gold-gradient-text">{unit.numero}</span>
            </Reveal>
            <GoldLine className="mt-8 w-24" />
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-lg sm:text-xl font-light text-[#A5A5A5] leading-relaxed">
                {unit.descrizione}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VISIONE */}
      <section className="bg-[#141414] py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="eyebrow mb-6">{t("bu.visione")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-[#F5F2EB] leading-snug tracking-tight">
              {unit.visione}
            </p>
          </Reveal>
          <GoldLine className="mt-10 w-32 mx-auto" />
          <Reveal delay={0.2}>
            <p className="mt-10 font-display text-2xl sm:text-3xl font-light italic gold-gradient-text">
              {unit.payoffFinale}
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA + NEXT */}
      <section className="bg-[#080808] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-center justify-between gap-12">
          <Reveal>
            <Link
              to="/diventa-partner"
              data-testid="bu-cta-partner"
              className="group inline-flex items-center gap-3 px-10 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] transition-colors duration-300"
            >
              {t("hero.ctaPartner")}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex items-center gap-8">
              <Link
                to="/#divisioni"
                data-testid="bu-back-units"
                className="group inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#A5A5A5] hover:text-[#C6A45D] transition-colors duration-300"
              >
                <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
                {t("bu.all")}
              </Link>
              <Link
                to={`/business-unit/${next.id}`}
                data-testid="bu-next-unit"
                className="group text-right"
              >
                <span className="block text-[10px] tracking-[0.3em] uppercase text-[#A5A5A5] mb-1">{t("bu.next")}</span>
                <span className="font-display text-2xl font-light text-[#F5F2EB] group-hover:text-[#E5C776] transition-colors duration-300">
                  Billions {next.nome} <ArrowRight size={15} className="inline transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
